import { Issue } from "../../../../models";
import { IssueDAO } from "../IssueDAO";
import ClientLogger from "../../../logging/ClientLogger";
import { ListResponse } from "../../../util/data/list/ListResponse";
import LoggerCommonTags from "../../../logging/LoggerCommonTags";
import ResourceNotFoundError from "../../../ResourceNotFoundError";
import { SortDirection } from "aws-amplify";
import _ from "lodash";

export default class MockDataStoreIssueDAO implements IssueDAO {
    private static readonly STORAGE_NAME = "mock-issues";
    private static DELETE_ATTEMPT_METRIC_NAME = "MockDataStoreIssueDAO.Delete.Attempt"
    private static DELETE_FAILURE_METRIC_NAME = "MockDataStoreIssueDAO.Delete.Failure"

    private logger: ClientLogger;

    constructor(
        logger: ClientLogger
    ) {
        this.logger = logger;
    }

    private loadMock(): Issue[] {
        let mock = new Array<Issue>();
        const mockStr = localStorage.getItem(MockDataStoreIssueDAO.STORAGE_NAME);

        if (mockStr) {
            mock = JSON.parse(mockStr) as Issue[];
        }

        return mock;
    }

    private saveMock(mock: Issue[]): void {
        localStorage.setItem(MockDataStoreIssueDAO.STORAGE_NAME, JSON.stringify(mock));
    }

    async getIssueById(issueId: string): Promise<Issue> {
        const mock = this.loadMock();
        const result = mock.find((issue): boolean => {
            return issue.id === issueId;
        });
        
        if (!result) {
            throw new ResourceNotFoundError("Issue");
        }
        return result;
    }

    public async getIssuesByLocationIdWithLimitAndSortDirection(
        locationId: string,
        sortDirection: SortDirection,
        limit: number
    ): Promise<Array<Issue>> {
        try {
            this.logger.info(
                `Fetching Issues with sortDirection and limit for: ${locationId}`,
                {},
                [LoggerCommonTags.ISSUE_LIST_RETRIEVAL_BY_LOCATION_ID_ATTEMPT]
            );
            
            const mock = this.loadMock();
            let filteredIssues = mock.filter((issue): boolean => {
                return issue.locationId === locationId;
            });

            // Sort by creation date
            filteredIssues.sort((left, right): number => {
                const leftDate = left.clientCreationDate ?? 0;
                const rightDate = right.clientCreationDate ?? 0;
                
                if (sortDirection === SortDirection.ASCENDING) {
                    return leftDate - rightDate;
                } else {
                    return rightDate - leftDate;
                }
            });

            return filteredIssues.slice(0, limit);
        } catch (error) {
            this.logger.error(
                `Error occurred while retrieving Issues by Location with sortDirection and limit for: ${locationId}`,
                error,
                [LoggerCommonTags.ISSUE_LIST_RETRIEVAL_FAILURE]
            );
            throw error;
        }
    }

    public async getIssuesByLocationId(locationId: string): Promise<Array<Issue>> {
        try {
            this.logger.info(
                `Fetching Issues for: ${locationId}`,
                {},
                [LoggerCommonTags.ISSUE_LIST_RETRIEVAL_BY_LOCATION_ID_ATTEMPT]
            );
            
            const mock = this.loadMock();
            const filteredIssues = mock.filter((issue): boolean => {
                return issue.locationId === locationId;
            });
            
            return filteredIssues;
        } catch (error) {
            this.logger.error(
                `Error occurred while retrieving Issues by Location for: ${locationId}`,
                error,
                [LoggerCommonTags.ISSUE_LIST_RETRIEVAL_FAILURE]
            );
            throw error;
        }
    }

    public async getIssuesByPropertyId(propertyId: string): Promise<ListResponse<Issue>> {
        const mock = this.loadMock();
        const issues = mock.filter((issue): boolean => {
            return issue.propertyId === propertyId;
        });
        
        return { items: issues, nextToken: null };
    }

    public async createNewIssue(issue: Issue): Promise<Issue> {
        try {
            const issueToCreate = new Issue({
                ...issue,
                clientCreationDate: Date.now(),
                localLastUpdatedTime: Date.now()
            });
            
            const mock = this.loadMock();
            const exists = mock.find((value): boolean => {
                return value.id === issueToCreate.id;
            });

            if (exists) {
                throw Error("Issue already exists");
            }

            mock.push(issueToCreate);
            this.saveMock(mock);

            return issueToCreate;
        } catch (error) {
            throw new Error("Failed to create the issue");
        }
    }

    public async editIssue(issueId: string, issue: Issue): Promise<Issue> {
        try {
            const existingIssue: Issue = await this.getIssueById(issueId);
            let result: Issue = {
                ...existingIssue,
                name: issue.name,
                locationId: issue.locationId,
                note: issue.note,
                propertyId: issue.propertyId,
                status: issue.status,
                localLastUpdatedTime: Date.now()
            };

            if (!_.isEqual(existingIssue, result)) {
                const mock = this.loadMock();
                const index = mock.findIndex((value): boolean => {
                    return value.id === result.id;
                });

                if (index >= 0) {
                    mock[index] = result;
                    this.saveMock(mock);
                }
            } else {
                result = existingIssue;
            }
            
            return result;
        } catch (error) {
            throw new Error("Failed to update the issue");
        }
    }

    public async deleteIssue(issueId: string): Promise<void> {
        this.logger.info(
            `Deleting issue with id: ${issueId}`,
            {},
            [MockDataStoreIssueDAO.DELETE_ATTEMPT_METRIC_NAME]
        );
        try {
            const mock = this.loadMock();
            const filteredMock = mock.filter((issue): boolean => {
                return issue.id !== issueId;
            });
            
            this.saveMock(filteredMock);
        } catch (error) {
            this.logger.error(
                `Failed to delete issue with id: ${issueId}`,
                error,
                [MockDataStoreIssueDAO.DELETE_FAILURE_METRIC_NAME]
            );
            throw error;
        }
    }
} 